import { Dialog, Listbox, Transition } from "@headlessui/react";
import { Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import React, { Fragment, useContext, useMemo, useState } from "react";
import Layout from "../../components/layout/layout";
import { formatPrice, parseStory, responsiveGet } from "../../components/utils";
import arrowD from "../../resources/images/icon_arrow_down.svg";
import closeIc from "../../resources/images/close.svg";
import useWindowSize from "../../components/use-window-size";
import RichtextBlok from "../../components/storyblok/richtext-blok";
import { EmailContext } from "../../components/context/email-context";
import diffIc from "../../resources/images/icon_difficulty@1x.svg";
import persIc from "../../resources/images/icon_avatar@1x.svg";
import timeIc from "../../resources/images/icon_timing@1x.svg";
import notIc from "../../resources/images/icon_notification@1x.svg";
import kitIc from "../../resources/images/icon_kit@1x.svg";
import emailIc from "../../resources/images/icon_email@1x.svg";
import LinedTitle from "../../components/commons/lined-title";
import PageHeader from "../../components/page-header";
import ispirazioniAnim from "../../resources/images/headers/ispirazioni.json";
import { LanguageContext } from "../../context/language-context";
import useShopifyProducts from "../use-shopify-products";
import Button from "../button";
import { StoreContext } from "../../context/store-context";

const getUrlState = (location) => {
  const urlSearchParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  return params;
};

const getInitKit = (urlState, kits) => {
  const matchingKit = kits.filter((k) => k[0] === (urlState?.kit ?? ""));
  return matchingKit.length === 1 ? matchingKit[0] : kits[0];
};

const IspPage = ({ data, location }) => {
  const { t, locale } = useContext(LanguageContext);
  const l = locale;

  const kits = [
    ["kit", t.kit],
    ["together", "Kit Together"],
    ["family", "Kit Family"],
    ["baby", "Kit Baby"],
    ["unique", "Kit Unique"],
  ];

  const difficulties = ["difficolta", "bassa", "media", "alta"].map((v) => [
    v,
    t[v],
  ]);

  const adulti = ["adulti", ...[...Array(6).keys()]].map((v) => [
    v,
    `${v} ${t["adult" + (v === 1 ? "o" : "i")]}`,
  ]);

  const bambini = ["bambini", ...[...Array(6).keys()]].map((v) => [
    v,
    `${v} ${t["bambin" + (v === 1 ? "o" : "i")]}`,
  ]);

  const ispirazioni = (data?.ispirazioni?.nodes ?? []).map((i) =>
    parseStory(i)
  );
  const [urlState] = useState(getUrlState(location));
  const [kit, setKit] = useState(getInitKit(urlState, kits));
  const [diff, setDiff] = useState(difficulties[0]);
  const [adulto, setAdulto] = useState(adulti[0]);
  const [bambino, setBambino] = useState(bambini[0]);

  console.log({
    kit,
    diff,
    adulto,
    bambino,
    isp: ispirazioni.map((i) => i.content.adulti),
  });

  const filterIspirazioni = (i) =>
    (kit[0] === "kit" || i.content.kit === kit[0]) &&
    (diff[0] === "difficolta" ||
      i.content.difficolta === "" ||
      i.content.difficolta === diff[0]) &&
    (adulto[0] === "adulti" ||
      i.content.adulti === "" ||
      adulto[0] === parseInt(i.content.adulti, 10)) &&
    (bambino[0] === "bambini" ||
      i.content.bambini === "" ||
      bambino[0] === parseInt(i.content.bambini, 10));

  const activeIspirazioni = useMemo(
    () => ispirazioni.filter(filterIspirazioni),
    [ispirazioni, kit, diff, adulto, bambino]
  );

  const { isMobile, width } = useWindowSize();

  return (
    <Layout>
      <PageHeader
        bg="bg-ci-beige"
        img="green"
        tit={t["isp-tit"]}
        sub={t["isp-occhiello"]}
        anim={ispirazioniAnim}
        posInfo={responsiveGet(width, {
          def: { right: "-30px", top: "10px" },
          md: { top: "17px", right: "-230px" },
          lg: { right: "-60px", top: "10px" },
        })}
      />
      <div className="max-w-screen-xl mx-auto px-4 pb-64">
        <div className="flex items-end flex-wrap gap-14 my-14 justify-between">
          <div className="flex-1 mb-10">
            <h2 className="ci-h2">{t["isp-t1-tit"]}</h2>
            <p>{t["isp-t1-text"]}</p>
            <div className="mt-12 flex gap-5 items-center">
              <p>{t["filtra-per"]}</p>
              <FilterBox val={kit} vals={kits} setVal={setKit} />
              <FilterBox val={diff} vals={difficulties} setVal={setDiff} />
              <FilterBox val={adulto} vals={adulti} setVal={setAdulto} />
              <FilterBox val={bambino} vals={bambini} setVal={setBambino} />
            </div>
          </div>
          <div className="flex-1">
            <StaticImage
              src="../../resources/images/calco-family.png"
              loading="eager"
              formats={["webp", "auto"]}
              alt=""
              layout="constrained"
              width={580}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-8">
          {activeIspirazioni.map((i) => (
            <article
              key={i.slug}
              style={{
                width: isMobile ? "100%" : "calc(33.33% - 22px)",
                aspectRatio: "390 / 290",
              }}
            >
              <IspirazioneCard
                i={i}
                isFromInstructions={
                  urlState["from-instruction"] === "true" ? true : false
                }
              />
            </article>
          ))}
        </div>
      </div>
    </Layout>
  );
};

const FilterBox = ({ val, setVal, vals }) => (
  <div>
    <Listbox value={val} onChange={setVal} className="relative z-10">
      {({ open }) => (
        <>
          <Listbox.Button className="ci-grad py-2.5 px-4 flex items-center gap-4">
            <span>{val[1]}</span>
            <img
              src={arrowD}
              alt=""
              className={
                "w-6 transition-transform transform " +
                (open ? "rotate-180" : "")
              }
            />
          </Listbox.Button>
          <Transition
            show={open}
            unmount={false}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            className="absolute bg-gray-200 px-4 py-2.5 cursor-pointer z-10"
          >
            <Listbox.Options static>
              {vals.map((k) => (
                <Listbox.Option key={k[0]} value={k}>
                  {k[1]}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </>
      )}
    </Listbox>
  </div>
);

const IspirazioneCard = ({ i, isFromInstructions }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <>
      <Link
        to={`#${i.slug}`}
        className="block relative h-full transition-shadow shadow-none hover:shadow-ci-card cursor-pointer duration-300"
        onClick={() => {
          setIsModalOpen(true);
        }}
      >
        {i?.content?.preview?.filename ? (
          <img
            src={i.content.preview.filename}
            alt={i.content.preview?.alt ?? ""}
            className="object-cover h-full"
          />
        ) : (
          <StaticImage
            src="../../resources/images/placeholder.png"
            alt=""
            layout="fullWidth"
            imgClassName="h-full"
            className="h-full"
            placeholder="tracedSVG"
            formats={["webp", "auto"]}
          />
        )}
        {i?.content?.label && (
          <div
            className={
              "top-5 right-5 absolute ci-badge text-white bg-ci-" +
              (i.content?.color ?? "beige")
            }
          >
            {i.content.label}
          </div>
        )}
      </Link>
      {/* Modal open ispiration */}
      <InspirationPopup
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        i={i}
        isFromInstructions={isFromInstructions}
      />
    </>
  );
};

const getAdultiSentence = (a, b, t) => {
  let aSent = "",
    bSent = "";
  const congiunzione = a > 0 && b > 0 ? ` ${t.e} ` : "";
  if (a > 0) {
    aSent = `${a} ${t["adult" + (a === 1 ? "o" : "i")]}`;
  }
  if (b > 0) {
    bSent = `${b} ${t["bambin" + (b === 1 ? "o" : "i")]}`;
  }
  return aSent + congiunzione + bSent;
};

const InspirationPopup = ({ isOpen, setIsOpen, i, isFromInstructions }) => {
  const [activeImg, setActiveImg] = useState(0);
  const hasImgs = "images" in i.content && i.content.images.length > 0;
  const { t, locale } = useContext(LanguageContext);
  const l = locale;
  const adultiSentence = getAdultiSentence(
    i.content?.adulti ?? 0,
    i.content?.bambini ?? 0,
    t
  );

  const { email, setEmail } = useContext(EmailContext);
  const [tempEmail, setTempEmail] = useState(email);

  const [avail, setAvail] = useState(true);

  const prods = useShopifyProducts();
  // assume prodotti is comma-separated w/ / to indicate variants
  const ids = (i.content?.prodotti.trim() ?? "")
    .split(",")
    .map((p) => p.trim().split("/"));

  const getStorefrontIdOrNull = (i) => {
    const p = prods[i[0].toUpperCase()];
    if (!p) return null;
    const sku = (i.length > 1 ? i[1] : i[0]).toUpperCase();
    const [wantedV] = (p?.variants ?? [])
      .filter((v) => v?.sku.toUpperCase() === sku)
      .map((v) => [v?.storefrontId, v?.price]);
    return wantedV;
  };

  const storefrontIdsAndPrices = ids.map(getStorefrontIdOrNull);
  const isButtonDisabled =
    ids.length === 0 || ids.length !== storefrontIdsAndPrices.length || !avail;

  //console.log({ prods, ids, storefrontIdsAndPrices });

  const price = storefrontIdsAndPrices.reduce(
    (cum, nex) => cum + parseFloat((nex ?? [null, "0"])[1]),
    0
  );

  const storeCtx = useContext(StoreContext);

  const checkAndAdd = async () => {
    const allLive = await storeCtx.client.product.fetchAll();
    const availVars = allLive.reduce((obj, p) => {
      p.variants.forEach((v) => {
        v.available && obj.add(v.id);
      });
      return obj;
    }, new Set());
    const notAllAvail = storefrontIdsAndPrices.some(
      ([id, _]) => !availVars.has(id)
    );
    if (notAllAvail) {
      setAvail(false);
    } else {
      setIsOpen(false);
      storeCtx.addVariants(
        storefrontIdsAndPrices.map(([id, _]) => ({
          variantId: id,
          quantity: 1,
        }))
      );
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={() => {
          setIsOpen(false);
        }}
      >
        <div className="min-h-screen grid place-content-center w-full">
          <Transition.Child
            as={Fragment}
            enter="duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-70"
            entered="opacity-70"
            leave="duration-200"
            leaveFrom="opacity-70"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block px-4 max-w-screen-xl w-full transition-all transform">
              <div className="flex gap-4 bg-white shadow-ci-card p-4 items-start rounded-ci-button">
                {hasImgs && (
                  <>
                    <div className="flex md:flex-col gap-5">
                      {(i?.content?.images ?? []).map((img, idx) => (
                        <button
                          key={img.filename}
                          className="cursor-pointer w-20 h-20"
                          onClick={() => {
                            setActiveImg(idx);
                          }}
                        >
                          <img
                            src={img.filename}
                            alt={img?.alt ?? ""}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                    <img
                      src={i.content.images[activeImg].filename}
                      alt={i.content.images[activeImg]?.alt ?? ""}
                      className="max-w-full object-cover"
                      style={{ width: "46.5%", aspectRatio: "1" }}
                    />
                  </>
                )}
                <div>
                  <div className="flex justify-end">
                    <button
                      className="cursor-pointer"
                      onClick={() => {
                        setIsOpen(false);
                      }}
                    >
                      <img src={closeIc} alt="Chiudi l'ispirazione" />
                    </button>
                  </div>
                  <div className="pl-4 pr-10">
                    <Dialog.Title as="h3" className="ci-h3">
                      {t["realizza-ricordo"]}:
                    </Dialog.Title>
                    {i?.content?.text && (
                      <div className="mt-4">
                        <RichtextBlok data={i.content.text} />
                      </div>
                    )}
                    <div className="mt-8 flex gap-x-5 gap-y-3 flex-wrap items-center text-sm whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <img src={kitIc} alt="" />
                        <p>
                          Kit{" "}
                          <span className="font-bold capitalize">
                            {i.content?.kit ?? "sconosciuto"}
                          </span>
                        </p>
                      </div>
                      <div className=" flex items-center gap-2.5">
                        <img src={persIc} alt="" />
                        <p>
                          {t["ideale-per"]}{" "}
                          <span className="font-bold">{adultiSentence}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <img src={diffIc} alt="" />
                        <p>
                          {t.difficolta}{" "}
                          <span className="font-bold capitalize">
                            {t[i.content?.difficolta] ?? "sconosciuta"}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <img src={timeIc} alt="" />
                        <p>
                          {t["tempo-realizzazione"]}{" "}
                          <span className="font-bold">
                            {i.content?.durata ?? "??"}min
                          </span>
                        </p>
                      </div>
                    </div>
                    {!isFromInstructions && (
                      <div className="flex mt-12 gap-4 items-center">
                        <div>
                          <p className="font-bold text-2xl">
                            € {formatPrice(price, l)}
                          </p>
                          <p className="text-ci-gray mt-2">
                            {t["iva-inclusa"]}
                          </p>
                        </div>
                        <button
                          disabled={isButtonDisabled}
                          onClick={checkAndAdd}
                        >
                          <Button type="red">
                            {
                              t[
                                isButtonDisabled
                                  ? "alcuni-non-disponibili"
                                  : "aggiungi-tutto"
                              ]
                            }
                          </Button>
                        </button>
                      </div>
                    )}
                    {i.content?.has_tutorial && (
                      <div className="mt-10">
                        <LinedTitle>
                          <img src={emailIc} alt="" />
                          <p>{t["ricevi-tutorial"]}</p>
                        </LinedTitle>
                        <form
                          className="mt-4"
                          onSubmit={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <div className="relative">
                            <input
                              type="email"
                              placeholder="Email"
                              className="block border rounded-full w-full pl-8 pr-52 py-3 text-base outline-none focus:ring-black focus:ring-1 border-ci-gray-light"
                              required
                              value={tempEmail}
                              onChange={(e) => {
                                setTempEmail(e.target.value);
                              }}
                            />
                            <button className="h-full absolute top-0 right-0 w-36 bg-ci-gray text-white uppercase rounded-full font-bold">
                              {t.ottieni}
                            </button>
                          </div>
                          <label className="mt-4 flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" required />
                            <p className="text-xs">{t["newsletter-terms"]}</p>
                          </label>
                        </form>
                      </div>
                    )}
                    {i.content?.has_extra && (
                      <div className="mt-5 flex gap-4 items-start">
                        <img src={notIc} alt="" />
                        <p className="text-xs">{t["disclaimer-extra"]}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default IspPage;
